/** English UI copy for legal notices and registration consents (all jurisdictions). */

export const LEGAL_COPY_EN = {
  toggleCollapsed: 'Read full notice',
  toggleExpanded: 'Hide full notice',
  fieldRequired: 'This field is required',
  consents: {
    AU: {
      privacyLink: 'Privacy Collection Notice',
      privacy:
        'I have read and understood the Domio.world Privacy Collection Notice and consent to the collection, use, and storage of my personal information as described therein.',
      sensitive:
        'I consent to Domio.world collecting and handling my sensitive personal information (including identity documents and financial records) for the purposes described in the Privacy Collection Notice.',
    },
    CO: {
      privacyLink: 'Privacy Notice',
      privacy:
        'I have read and understood the Domio.world Privacy Notice and authorize the processing of my personal data for the purposes stated therein, in accordance with Law 1581 of 2012 (Colombia).',
      sensitive:
        'I expressly authorize the processing of my sensitive data (identity documents and financial records) for the purposes described in the Privacy Notice.',
    },
    ES: {
      privacyLink: 'Data Protection Information',
      privacy:
        'I have read and accept the Domio.world Data Protection Information and consent to the processing of my personal data for the provision of the service, in accordance with GDPR (EU) 2016/679 and LOPDGDD 3/2018.',
      marketing:
        'I agree to receive commercial communications from Domio.world about news, offers, and service updates. I may withdraw this consent at any time.',
      sensitive:
        'I consent to the processing of my identification and financial data (special categories) for identity verification and management of lease agreements.',
    },
  },
  notices: {
    AU: {
      sections: [
        {
          title: 'Domio.world — Privacy Collection Notice',
          paragraphs: [
            'This Privacy Collection Notice explains how Domio.world (we, us, our) collects, uses, stores, and discloses your personal information when you register for and use our property management platform.',
          ],
        },
        {
          title: 'What personal information we collect',
          paragraphs: [
            'We may collect: identity and contact details (name, email address, phone number, role); account credentials (hashed passwords); property, tenancy, and financial information you enter or upload; identity documents and financial records where you provide them for verification or AI document processing; usage, device, and log data; communications with us; and marketing preferences where you opt in.',
          ],
        },
        {
          title: 'How we collect your personal information',
          paragraphs: [
            'We collect information directly from you when you register, complete your profile, upload documents, or use platform features. We may also collect information automatically through cookies, analytics, and server logs, and from integrated services you authorize (such as email or storage providers).',
          ],
        },
        {
          title: 'Why we collect, use and disclose personal information',
          paragraphs: [
            'We use your information to provide and improve the Domio.world service; create and manage your account; process property, tenancy, maintenance, and financial records; run AI-assisted document extraction where enabled; send service and security notifications; comply with legal obligations; and, with your consent, send marketing communications. We disclose information to service providers who assist us (hosting, email, analytics, AI processing) under contractual privacy obligations, and where required by law.',
          ],
        },
        {
          title: 'Who we disclose personal information to',
          paragraphs: [
            'Disclosures may be made to: cloud infrastructure and database providers; email delivery providers; AI and document-processing partners; professional advisers; regulators and law enforcement where required; and other users within your organization or property portfolio according to your role and permissions.',
          ],
        },
        {
          title: 'Overseas disclosure',
          paragraphs: [
            'Your personal information may be stored or processed in Australia and other countries where our service providers operate. Where we disclose personal information overseas, we take reasonable steps to ensure recipients handle it in a manner consistent with the Australian Privacy Principles, including contractual safeguards where appropriate.',
          ],
        },
        {
          title: 'Storage and security',
          paragraphs: [
            'We retain personal information for as long as your account is active and as needed to provide the service, meet legal obligations, and resolve disputes. We implement technical and organizational measures including encryption in transit, access controls, and audit logging. No method of transmission or storage is completely secure.',
          ],
        },
        {
          title: 'Your rights',
          paragraphs: [
            'Subject to applicable law, you may request access to, correction of, or deletion of your personal information, and withdraw consent where processing is consent-based. Withdrawal may limit your ability to use certain features. You may complain to us if you believe we have mishandled your information.',
          ],
        },
        {
          title: 'Complaints',
          paragraphs: [
            'If you have a privacy complaint, contact our Privacy Officer using the details below. We will acknowledge your complaint and respond within a reasonable period. If you are not satisfied with our response, you may contact the Office of the Australian Information Commissioner (OAIC) at www.oaic.gov.au.',
          ],
        },
        {
          title: 'Contact our Privacy Officer',
          paragraphs: [
            'Privacy Officer — Domio.world',
            'Email: privacy@domio.world',
            'Website: https://domio.world',
          ],
        },
      ],
    },
    CO: {
      sections: [
        {
          title: 'Privacy Notice — Domio.world',
          paragraphs: [
            'In compliance with Law 1581 of 2012 and Decree 1377 of 2013 (Colombia), Domio.world informs data subjects about the processing of their personal information.',
          ],
        },
        {
          title: '1. Data controller',
          paragraphs: [
            'Controller: Domio.world. Data protection / privacy contact: privacy@domio.world. Website: https://domio.world.',
          ],
        },
        {
          title: '2. Personal data we collect',
          paragraphs: [
            'Identification and contact data (name, email, phone, role); account credentials; property, tenancy, maintenance, and financial information; identity documents and financial records you upload; usage and technical log data; and communication preferences.',
          ],
        },
        {
          title: '3. Purposes of processing',
          paragraphs: [
            'Provision of the property management service; account creation and administration; document processing and authorized AI features; operational and security notifications; compliance with legal obligations; and, with express authorization, commercial communications.',
          ],
        },
        {
          title: '4. Data subject rights',
          paragraphs: [
            'You may access, update, rectify, and delete your data; revoke authorization and/or request deletion when there is no legal or contractual duty to retain it; and file complaints with the Superintendence of Industry and Commerce (SIC).',
          ],
        },
        {
          title: '5. Transfer and disclosure',
          paragraphs: [
            'We may transfer data to hosting, email, analytics, and document-processing providers located in Colombia or abroad under contracts requiring adequate protection. International transfer will comply with applicable law.',
          ],
        },
        {
          title: '6. Inquiries and complaints procedure',
          paragraphs: [
            'To exercise your rights or submit inquiries and complaints, write to privacy@domio.world with your name, ID, and request. We will respond within legal timeframes. If dissatisfied, you may contact the SIC: www.sic.gov.co.',
          ],
        },
      ],
    },
    ES: {
      sections: [
        {
          title: 'Data Protection Information — Domio.world',
          paragraphs: [
            'In accordance with Regulation (EU) 2016/679 (GDPR) and Organic Law 3/2018 on the protection of personal data and guarantee of digital rights (LOPDGDD), we inform you about the processing of your personal data.',
          ],
        },
        {
          title: '1. Data controller',
          paragraphs: [
            'Controller: Domio.world. Contact: privacy@domio.world. Data Protection Officer (if applicable): privacy@domio.world.',
          ],
        },
        {
          title: '2. Data we process',
          paragraphs: [
            'Identification and contact data; access credentials; data relating to properties, tenancies, and finances; identity documents and financial data for verification; usage data; and, where applicable, data for commercial communications with your consent.',
          ],
        },
        {
          title: '3. Purposes and legal basis',
          paragraphs: [
            'Performance of the service contract (Art. 6.1.b GDPR); compliance with legal obligations (Art. 6.1.c); legitimate interest in security and service improvement (Art. 6.1.f); consent for sensitive data, marketing, and optional processing (Art. 6.1.a and Art. 9.2.a where applicable).',
          ],
        },
        {
          title: '4. Recipients and processors',
          paragraphs: [
            'We may disclose data to infrastructure, email, analytics, and document/AI processing providers acting as processors under Art. 28 GDPR, as well as competent authorities when required by law.',
          ],
        },
        {
          title: '5. International transfers',
          paragraphs: [
            'If data is transferred outside the European Economic Area, we will adopt appropriate safeguards (standard contractual clauses, adequacy decisions, or other measures under the GDPR).',
          ],
        },
        {
          title: '6. Retention periods',
          paragraphs: [
            'We will retain data while you maintain your account and for periods necessary for legal obligations, claims, and audit. Marketing data will be retained until you withdraw consent.',
          ],
        },
        {
          title: '7. Rights and complaints',
          paragraphs: [
            'You may exercise rights of access, rectification, erasure, restriction, objection, and portability, and withdraw consent at any time by writing to privacy@domio.world. You have the right to lodge a complaint with the Spanish Data Protection Agency (www.aepd.es).',
          ],
        },
      ],
    },
  },
};
